import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CONFIGURATION_AREA_LABELS,
  CONFIGURATION_SEVERITY_LABELS,
  configurationCheckApi,
  type ConfigurationCheckResult,
  type ConfigurationIssue,
  type ConfigurationSeverity,
} from '../api/configurationCheck'
import { Page, PageHeader, Button, ButtonLink, Callout, LoadingState, Card, CardHeader, DataTable, StatusPill } from '../components/ui'

/**
 * F-122 (TASK-0376, REQ-294-001): verifica della configurazione. Mostra, per area, i riferimenti rotti, le regole
 * incompatibili e i flussi senza uscita trovati nelle configurazioni attive o pubblicate. Sola lettura: non
 * corregge e non blocca nulla. Pagina solo Admin (il server risponde 403 agli altri).
 */
const SEVERITIES: ConfigurationSeverity[] = ['errore', 'avviso', 'info']

const TONE: Record<ConfigurationSeverity, 'error' | 'warning' | 'neutral'> = {
  errore: 'error',
  avviso: 'warning',
  info: 'neutral',
}

export function ConfigurationCheckPage() {
  const [result, setResult] = useState<ConfigurationCheckResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [severity, setSeverity] = useState<ConfigurationSeverity | 'all'>('all')

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setResult(await configurationCheckApi.run())
    } catch (e) {
      setResult(null)
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const groups = useMemo(() => {
    const map = new Map<string, ConfigurationIssue[]>()
    for (const issue of result?.issues ?? []) {
      if (severity !== 'all' && issue.severity !== severity) continue
      const list = map.get(issue.area) ?? []
      list.push(issue)
      map.set(issue.area, list)
    }
    return [...map.entries()]
  }, [result, severity])

  return (
    <Page>
      <PageHeader
        title="Verifica configurazione"
        icon="rule_folder"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          <Button variant="outlined" onClick={() => void load()} disabled={loading}>
            Ricontrolla
          </Button>
        }
      />

      <Callout tone="info">
        Cerca riferimenti rotti (utenti, reparti, prodotti, campagne), regole incompatibili con il loro evento o con il
        vocabolario e flussi senza uscita, solo nelle configurazioni attive o pubblicate. La verifica legge e non
        modifica nulla: correggere resta una scelta dell'amministratore. Le voci informative (ad esempio il mapping
        verso gli operatori, BLK-002) non sono errori.
      </Callout>

      {error && <Callout tone="error">{error}</Callout>}

      {loading ? (
        <LoadingState />
      ) : result ? (
        <>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Button
              variant={severity === 'all' ? 'filled' : 'outlined'}
              size="sm"
              onClick={() => setSeverity('all')}
            >
              Tutti ({result.issues.length})
            </Button>
            {SEVERITIES.map(s => (
              <Button
                key={s}
                variant={severity === s ? 'filled' : 'outlined'}
                size="sm"
                onClick={() => setSeverity(s)}
              >
                {CONFIGURATION_SEVERITY_LABELS[s]} ({result.counts[s] ?? 0})
              </Button>
            ))}
            <span className="text-muted md-typescale-body-small ms-auto">
              Controllato il {new Date(result.checkedAt).toLocaleString('it-IT')}
            </span>
          </div>

          {groups.length === 0 ? (
            <Callout tone="success">
              {result.issues.length === 0
                ? 'Nessun problema trovato nelle configurazioni attive.'
                : 'Nessun problema con questo filtro.'}
            </Callout>
          ) : (
            groups.map(([area, issues]) => (
              <Card key={area} variant="outlined" padding="none">
                <CardHeader title={CONFIGURATION_AREA_LABELS[area] ?? area} subtitle={`${issues.length}`} />
                <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Gravità</th>
                      <th>Configurazione</th>
                      <th>Problema</th>
                      <th className="text-end" />
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map((i, idx) => (
                      <tr key={`${i.code}-${i.entityType}-${i.entityId}-${idx}`}>
                        <td><StatusPill tone={TONE[i.severity]}>{i.severity}</StatusPill></td>
                        <td>
                          <div className="md-typescale-emphasized-body-medium">{i.entityName}</div>
                          <div className="text-muted md-typescale-body-small">{i.entityType} #{i.entityId}</div>
                        </td>
                        <td>
                          {i.message}
                          <div className="text-muted md-typescale-body-small"><code>{i.code}</code></div>
                        </td>
                        <td className="text-end">
                          <ButtonLink to={i.link} variant="outlined" size="sm">Apri</ButtonLink>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </Card>
            ))
          )}
        </>
      ) : null}
    </Page>
  )
}
